/**
 * 滤镜菜单动作（M3-13 / DESIGN.md §5.4；对比 Photoshop 补全第一档缺口
 * 阶段 3 补了参数对话框 + 5 个新滤镜）。
 *
 * 除查找边缘（真实 Photoshop 里这个滤镜没有参数、点了直接跑）以外，
 * 每一项都是"打开通用参数对话框，确认后带参数调用对应的 applyXxx"——
 * 高斯模糊/USM 锐化这两个既有滤镜也顺手接上了对话框，不再是硬编码参数
 * （这是一个既有缺口，不是新滤镜专属的）。
 */
import type { ActionRegistry } from './registry';
import type { AppStore } from '../store';
import { applyGaussianBlur, applyUnsharpMask, applyMotionBlur, applySurfaceBlur, applyAddNoise, applyEmboss, applyFindEdges } from '../filters';

/** 通用滤镜参数对话框（`ui/dialogs/FilterDialog.tsx`）认的这几种——类型定义放
 * 在应用层，UI 层从这里 import，不能反过来（分层规则只允许上层依赖下层）。 */
export type FilterKind = 'gaussianBlur' | 'unsharpMask' | 'motionBlur' | 'surfaceBlur' | 'addNoise' | 'emboss';

export interface FilterActionDeps {
  readonly store: AppStore;
  /** 打开通用滤镜参数对话框，记下这次是哪种滤镜。 */
  readonly openDialog: (kind: FilterKind) => void;
}

/** 不带省略号的名字——菜单标题额外加"…"，完成提示（hint）用这份原名。 */
export const FILTER_NAMES: Readonly<Record<FilterKind, string>> = {
  gaussianBlur: '高斯模糊',
  unsharpMask: 'USM 锐化',
  motionBlur: '动感模糊',
  surfaceBlur: '表面模糊',
  addNoise: '添加杂色',
  emboss: '浮雕',
};

const TITLES: Readonly<Record<FilterKind, string>> = Object.fromEntries(
  Object.entries(FILTER_NAMES).map(([kind, name]) => [kind, `${name}…`]),
) as Readonly<Record<FilterKind, string>>;

export function registerFilterActions(registry: ActionRegistry, deps: FilterActionDeps): void {
  const kinds: readonly FilterKind[] = [
    'gaussianBlur',
    'unsharpMask',
    'motionBlur',
    'surfaceBlur',
    'addNoise',
    'emboss',
  ];
  kinds.forEach((kind, order) => {
    registry.register({
      id: `filter.${kind}`,
      title: TITLES[kind],
      group: '滤镜',
      section: 0,
      order,
      enabled: (s) => s.hasDocument,
      run: () => {
        deps.openDialog(kind);
      },
    });
  });

  registry.register({
    id: 'filter.findEdges',
    title: '查找边缘',
    group: '滤镜',
    section: 0,
    order: kinds.length,
    enabled: (s) => s.hasDocument,
    run: async (services) => {
      const done = await applyFindEdges(deps.store);
      if (done) services.hint('已应用查找边缘');
    },
  });
}

/** 对话框确认后按 kind 分派到对应的 applyXxx——`FilterDialog` 交回的是通用 number 记录，这里做类型收窄。 */
export async function runFilterWithParams(
  store: AppStore,
  kind: FilterKind,
  params: Readonly<Record<string, number>>,
): Promise<boolean> {
  const num = (key: string, fallback: number): number => params[key] ?? fallback;
  switch (kind) {
    case 'gaussianBlur':
      return applyGaussianBlur(store, num('radius', 8));
    case 'unsharpMask':
      return applyUnsharpMask(store, {
        amount: num('amount', 60) / 100,
        radius: num('radius', 3),
        threshold: num('threshold', 4),
      });
    case 'motionBlur':
      return applyMotionBlur(store, { angle: num('angle', 0), distance: num('distance', 20) });
    case 'surfaceBlur':
      return applySurfaceBlur(store, { radius: num('radius', 5), threshold: num('threshold', 15) });
    case 'addNoise':
      return applyAddNoise(store, { amount: num('amount', 10), monochrome: num('monochrome', 0) !== 0 });
    case 'emboss':
      return applyEmboss(store, { angle: num('angle', 135), height: num('height', 3), amount: num('amount', 100) });
  }
}
